export const ADMIN_SESSION_TIMEOUT_MS = 20 * 60 * 1000;
export const ADMIN_SESSION_ACTIVITY_KEY = "fliplottery:admin:last-activity";
export const ADMIN_SESSION_LOGOUT_KEY = "fliplottery:admin:logout";
export const ADMIN_SESSION_FLASH_KEY = "fliplottery:admin:flash-message";
export const ADMIN_SESSION_CHANNEL_NAME = "fliplottery:admin:session";
export const ADMIN_SESSION_EXPIRED_MESSAGE =
  "Admin session expired due to inactivity. Please log in again.";
export const ADMIN_AUTH_EXPIRED_MESSAGE = "Admin session expired. Please log in again.";
export const API_SESSION_EXPIRED_MESSAGE = "Session expired due to inactivity.";
export const ACCESS_RESTRICTION_CODE = "ACCESS_TEMPORARILY_RESTRICTED";

const ADMIN_SESSION_ERROR_MESSAGES = new Set([
  API_SESSION_EXPIRED_MESSAGE,
  "Admin authentication required",
  "Invalid admin token",
  "Admin refresh session expired",
  "Admin refresh session not found"
]);

export const ADMIN_PROTECTED_PATH_PREFIX = "/admin";
export const ADMIN_PUBLIC_PATHS = new Set([
  "/",
  "/forgot-password",
  "/verify-reset-otp",
  "/reset-password"
]);

export const isProtectedAdminPath = (pathname = "") =>
  pathname.startsWith(ADMIN_PROTECTED_PATH_PREFIX);

export const getRemainingSessionMs = (lastActivityAt, timeoutMs = ADMIN_SESSION_TIMEOUT_MS) => {
  const lastActivityMs = Number(lastActivityAt || 0);

  if (!Number.isFinite(lastActivityMs) || lastActivityMs <= 0) {
    return timeoutMs;
  }

  return Math.max(timeoutMs - (Date.now() - lastActivityMs), 0);
};

export const shouldHandleAdminSessionExpiry = (responsePayload, statusCode) => {
  const message = String(responsePayload?.message || "").trim();
  return (
    (statusCode === 401 && ADMIN_SESSION_ERROR_MESSAGES.has(message)) ||
    (statusCode === 403 && responsePayload?.code === ACCESS_RESTRICTION_CODE)
  );
};

export const getAdminSessionExpiryMessage = (responsePayload) =>
  responsePayload?.code === ACCESS_RESTRICTION_CODE
    ? String(responsePayload?.message || ADMIN_AUTH_EXPIRED_MESSAGE).trim()
    : String(responsePayload?.message || "").trim() === API_SESSION_EXPIRED_MESSAGE
    ? ADMIN_SESSION_EXPIRED_MESSAGE
    : ADMIN_AUTH_EXPIRED_MESSAGE;

export const persistAdminSessionMessage = (message) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(ADMIN_SESSION_FLASH_KEY, message);
};

export const consumeAdminSessionMessage = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const message = window.sessionStorage.getItem(ADMIN_SESSION_FLASH_KEY) || "";
  if (message) {
    window.sessionStorage.removeItem(ADMIN_SESSION_FLASH_KEY);
  }
  return message;
};

export const publishAdminActivity = (timestamp = Date.now()) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(ADMIN_SESSION_ACTIVITY_KEY, String(timestamp));
};

export const clearAdminSessionActivity = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(ADMIN_SESSION_ACTIVITY_KEY);
};

export const broadcastAdminLogout = ({ reason = "manual", message = "" } = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  if (message) {
    persistAdminSessionMessage(message);
  }

  window.localStorage.setItem(
    ADMIN_SESSION_LOGOUT_KEY,
    JSON.stringify({
      reason,
      message,
      timestamp: Date.now()
    })
  );
};

export const createAdminSessionChannel = () => {
  if (typeof window === "undefined" || typeof window.BroadcastChannel === "undefined") {
    return null;
  }

  return new window.BroadcastChannel(ADMIN_SESSION_CHANNEL_NAME);
};
